import Button from "../components/Button";
import SGLogoIcon from "/public/images/icons/sgLogoIconNoFill.svg?react";
import TextInput from "../components/TextInput";
import { useEffect, useState } from "react";
import { useDrawerContext } from "../context/drawerContext";
import { useUserContext } from "../context/userContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import CloseIcon from "/public/images/icons/close.svg?react";
import { useThemeContext } from "../context/themeContext";
import SelectInput from "../components/SelectInput";
import { passwordStrength } from 'check-password-strength'
import SplashScreen from '../components/SplashScreen'
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { fetchWithRefresh } from "../utils/fetchWithRefresh";
import { LogErrors } from "../utils/LogErrors";

export default function Login() {
  const [errors, setErrors] = useState({});
  const [confirmationSent, setConfirmationSent] = useState(null);
  const [loginType, setLoginType] = useState("email");
  const { setOpen, setDrawerContent } = useDrawerContext();
  const [email, setEmail] = useState("");
  const { user, login, setSelectedVenue, setSelectedZone, splashScreen, logout, updateTokens } = useUserContext();
  const [ localUser, setLocalUser ] = useState(null);
  const navigate = useNavigate();
  const { theme, setTheme } = useThemeContext();
  const [ passStrength, setPassStrength ] = useState('Weak');
  const [ password, setPassword ] = useState('');
  const [ passTryCount, setPassTryCount ] = useState(0);
  const [ emailAuthenticated, setEmailAuthenticated ] = useState(false);
  const [otpUserLoginId, setOtpUserLoginId] = useState(localStorage.getItem("otpDrawerUserLoginId") || "");
  const location = useLocation();
  const reset = location.state?.reset || false;
  const [openOTPDrawer, setOpenOTPDrawer] = useState(localStorage.getItem("otpDrawerOpen") || false);
  const [userPhoneMasked, setUserPhoneMasked] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [otpSubmitting, setOtpSubmitting] = useState(false);

  const findFirstValueByKeys = (obj, keys) => {
    if (!obj || typeof obj !== "object") return null;
    for (const key of keys) {
      if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") {
        return obj[key];
      }
    }
    for (const value of Object.values(obj)) {
      if (value && typeof value === "object") {
        const found = findFirstValueByKeys(value, keys);
        if (found !== null && found !== undefined && found !== "") {
          return found;
        }
      }
    }
    return null;
  };

  const extractUserLoginId = (parsedRes, res) => {
    const idKeys = [
      "userLoginId",
      "UserLoginId",
      "UserLoginID",
      "userLoginID",
      "id",
      "Id",
      "ID",
      "userId",
      "UserId",
      "UserID",
      "loginId",
      "LoginId",
    ];

    return (
      findFirstValueByKeys(parsedRes, idKeys) ||
      findFirstValueByKeys(res, idKeys) ||
      null
    );
  };

  useEffect(() => {
    if (user && !localUser) {
      setOpen(false);
      handlePostLoginDrawers(user);
    }
  }, [theme]);

  useEffect(() => {
    if((localStorage.getItem("otpDrawerEmail") && openOTPDrawer)){
      const storedOtpUserLoginId = localStorage.getItem("otpDrawerUserLoginId");
      if (storedOtpUserLoginId) {
        setOtpUserLoginId(storedOtpUserLoginId);
      }
      triggerOtpDrawer(localStorage.getItem("otpDrawerEmail"));
    };
  }, [])

  useEffect(() => {
    if (user?.id) {
      fetchUserPhoneMasked(user?.email || user?.Email || email || localStorage.getItem("otpDrawerEmail"));
    }
  }, [user?.id])

  useEffect(() => {
    const otpEmail = localStorage.getItem("otpDrawerEmail");
    if (otpEmail && openOTPDrawer) {
      setDrawerContent(buildOtpDrawerContent(otpEmail));
    }
  }, [userPhoneMasked]);

  useEffect(() => {
    const otpEmail = localStorage.getItem("otpDrawerEmail");
    if (otpEmail && openOTPDrawer) {
      setDrawerContent(buildOtpDrawerContent(otpEmail));
    }
  }, [smsSending, confirmationSent, otpSubmitting]);

  const handlePostLoginDrawers = async (user, effectiveTheme = theme) => {
    Sentry.startSpan({ name: "Post Login Drawers", op: "auth.process" }, async (span) => {
      try {
        setTheme("");
        const roles = JSON.parse(localUser?.userRoles || user?.userRoles);
  
        if ((roles.some(role => role.Role === "STAFF") && roles.some(role => role.Role === "USER")) && effectiveTheme === '') {
          triggerThemeSelectionDrawer(localUser);
        } else if (roles.some(role => role.Role === "STAFF") && effectiveTheme !== "participant") {
          setTheme("staff");
  
          const uniqueVenues = [
            ...new Set(
              roles
                .filter(role => role.VenueName) // Filter out roles with empty VenueName
                .map(role => ({ id: role.VenueId, name: role.VenueName }))
            ),
          ];
  
          if (uniqueVenues.length > 1) {
            triggerVenueSelectionDrawer(uniqueVenues);
          } else if (uniqueVenues.length === 1) {
            try {
              const venueReq = await fetch(
                import.meta.env.VITE_API_URL + `Venue/FetchVenue?OrgID=${uniqueVenues[0]?.id}`,
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("auth_session")}`,
                  },
                }
              );
  
              if (!venueReq.ok) {
                throw new Error("Failed to fetch venue data");
              }
  
              const venueRes = await venueReq.json();
  
              const zoneReq = await fetch(
                import.meta.env.VITE_API_URL + `Zone/FetchActiveOrgZones?OrgID=${uniqueVenues[0]?.id}`,
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("auth_session")}`,
                  },
                }
              );
  
              if (!zoneReq.ok) {
                throw new Error("Failed to fetch zones");
              }
  
              const zoneRes = await zoneReq.json();
              const venueWithZones = {
                ...venueRes,
                zones: zoneRes.Zones,
              };
  
              setSelectedVenue(venueWithZones);
  
              if (venueWithZones?.zones.length > 1) {
                setOpen(true);
                setDrawerContent(
                  <div className="w-full">
                    <div className="flex items-center justify-between w-full">
                      <br />
                      <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                        Choose Zone
                      </h2>
                      <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
                    </div>
                    <div className="mt-6 flex flex-col gap-4 justify-stretch">
                      <h3 className="text-[24px]">Please choose the zone you are currently located at</h3>
                      <p className="text-sportsDarkGrey">You can update your zone anytime from your home page.</p>
                      <form className="flex flex-col gap-4" onSubmit={setZone}>
                        <SelectInput
                          name="zoneSelect"
                          className="capitalize"
                          options={venueWithZones?.zones?.map(zone => (
                            <option key={zone.ZoneID} value={JSON.stringify(zone)}>
                              {zone.ZoneName}
                            </option>
                          ))}
                          label="Your Current Zone"
                          placeholder="Please Select"
                        />
                        <Button submit>Confirm & Continue</Button>
                      </form>
                    </div>
                  </div>
                );
              } else if (venueWithZones?.zones.length === 1) {
                setSelectedZone(venueWithZones?.zones[0]);
                navigate("/");
                setOpen(false);
                triggerNotificationPrompt();
              } else {
                console.warn("No zones found for venue.");
                Sentry.captureMessage("No zones found for venue", "warning");
              }
            } catch (error) {
              LogErrors(user, logout, updateTokens, "Error fetching venue/zones: " + error);
              Sentry.captureException(error);
            }
          } else {
            console.warn("No venues found for this user.");
            Sentry.captureMessage("No venues found for this user", "warning");
          }
        } else if (roles.some(role => role.Role === "USER") && effectiveTheme !== "staff") {
          setTheme("participant");
          navigate("/");
        }
  
        span.setStatus("ok");
      } catch (error) {
        LogErrors(user, logout, updateTokens, "Error in handlePostLoginDrawers: " + error);
        Sentry.captureException(error);
      }
    });
  };

  const triggerThemeSelectionDrawer = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            Choose Account
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => {setOpen(false); localStorage.removeItem("user");localStorage.removeItem("auth_session")}} />
        </div>
        <div className="mt-6 flex flex-col gap-4 justify-stretch">
          <h3 className="text-[24px]">
            It looks like you have both Staff and Participant accounts
          </h3>
          <p className=" text-sportsDarkGrey">
            Please choose the account you wish to continue logging in as. You
            can log out and switch accounts at any time.
          </p>
          <Button
            className="!bg-sportsBlue text-white"
            onClick={() => {
              setTheme("staff");
              setLocalUser(null);
              setOpen(false)
            }}
          >
            Continue as Staff
          </Button>
          <Button
            className="!bg-sportsYellow !text-sportsBlack"
            onClick={() => {
              setTheme("participant");
              setLocalUser(null);
              setOpen(false)
            }}
          >
            Continue as Participant
          </Button>
        </div>
      </div>
    );
  };

  const triggerVenueSelectionDrawer = (venues) => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            Choose Venue
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col gap-4 justify-stretch">
          <h3 className="text-[24px]">
          Please choose the venue you are currently located at
          </h3>
          <p className=" text-sportsDarkGrey">
          You can update your venue anytime from your home page.
          </p>
          <form className="flex flex-col gap-4" onSubmit={triggerZoneSelectionDrawer}>
          <SelectInput name="venueSelect" className={'capitalize'} options={venues.map(venue => (<option key={venue.id} value={venue.id}>{venue.name}</option>))} label={'Your Current Venue'} placeholder={'Please Select'} />
          <Button
            submit
          >
            Confirm & Continue
          </Button>
        </form>
        </div>
      </div>
    );
  };

  const triggerZoneSelectionDrawer = async (event) => {
    event.preventDefault();
    Sentry.startSpan({ name: "Trigger Zone Selection Drawer", op: "ui.action" }, async (span) => {
      try {
        const newDetails = new FormData(event.currentTarget);
        const venueId = newDetails.get("venueSelect");
        const userToken = JSON.parse(localStorage.getItem("user"))?.token;
  
        if (!venueId) {
          console.warn("No venue selected.");
          Sentry.captureMessage("No venue selected.", "warning");
          return;
        }
  
        const venueReq = await fetchWithRefresh(
          import.meta.env.VITE_API_URL + `Venue/FetchVenue?OrgID=${venueId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userToken}`,
            },
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        if (!venueReq.ok) {
          throw new Error(`Failed to fetch venue data. Status: ${venueReq.status}`);
        }
  
        const venueRes = await venueReq.json();
  
        const zoneReq = await fetchWithRefresh(
          import.meta.env.VITE_API_URL + `Zone/FetchActiveOrgZones?OrgID=${venueId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userToken}`,
            },
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        if (!zoneReq.ok) {
          throw new Error(`Failed to fetch zones. Status: ${zoneReq.status}`);
        }
  
        const zoneRes = await zoneReq.json();
        const venueWithZones = {
          ...venueRes,
          zones: zoneRes.Zones || [],
        };
  
        setSelectedVenue(venueWithZones);
        setOpen(true);
        setDrawerContent(
          <div className="w-full">
            <div className="flex items-center justify-between w-full">
              <br />
              <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                Choose Zone
              </h2>
              <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
            </div>
            <div className="mt-6 flex flex-col gap-4 justify-stretch">
              <h3 className="text-[24px]">
                Please choose the zone you are currently located at
              </h3>
              <p className="text-sportsDarkGrey">
                You can update your zone anytime from your home page.
              </p>
              <form className="flex flex-col gap-4" onSubmit={setZone}>
                <SelectInput
                  name="zoneSelect"
                  className="capitalize"
                  options={venueWithZones?.zones?.map(zone => (
                    <option key={zone.ZoneID} value={JSON.stringify(zone)}>
                      {zone.ZoneName}
                    </option>
                  ))}
                  label="Your Current Zone"
                  placeholder="Please Select"
                />
                <Button submit>Confirm & Continue</Button>
              </form>
            </div>
          </div>
        );
  
        span.setStatus("ok");
      } catch (error) {
        LogErrors(user, logout, updateTokens, "Error in triggerZoneSelectionDrawer: " + error);
        Sentry.captureException(error);
        toast.error("An error occurred while loading zones.");
      } finally {
        span.end();
      }
    });
  };
  

  const setZone = async (event) => {
    event.preventDefault();
    const newDetails = new FormData(event.currentTarget);
    setSelectedZone(JSON.parse(newDetails.get("zoneSelect")));
    navigate("/");
    setOpen(false);
    triggerNotificationPrompt();
  }

  const triggerNotificationPrompt = async () => {
    // Ask for permission to fire notifications
    await Notification.requestPermission().then((permission) => {
      // if (permission === 'granted') {
      //   LogErrors(user, logout, updateTokens, 'Notification permission granted.');
      // } else {
      //   LogErrors(user, logout, updateTokens, 'Unable to get permission to notify.');
      // }
    });
  }

  const fetchUserPhoneMasked = async (emailAddress) => {
    return await Sentry.startSpan({ name: "Fetch User Phone Masked", op: "http.request" }, async (span) => {
      try {
        span.setAttribute("userLoginId", user?.id || "unknown");
        span.setAttribute("email", emailAddress || "unknown");

        const targetEmail = emailAddress || email || localStorage.getItem("otpDrawerEmail") || user?.email || user?.Email;
        if (!targetEmail) {
          Sentry.captureMessage("FetchUserPhone skipped: missing email", "warning");
          return;
        }

        const req = await fetch(import.meta.env.VITE_API_URL + `Users/FetchUserPhoneMasked`, {
          method: "POST",
          body: JSON.stringify({
            email: targetEmail,
            mode: "last4",
          }),
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!req.ok) {
          const errorMessage = `Failed to fetch user phone. Status: ${req.status}`;
          Sentry.captureMessage(errorMessage, "warning");
          throw new Error(errorMessage);
        }

        const res = await req.json();
        const last4 = res?.last4 || res?.Last4 || res?.ladt4 || res?.masked || res?.phoneMasked || res?.PhoneMasked || "";
        setUserPhoneMasked(String(last4));
        span.setStatus("ok");
      } catch (error) {
        LogErrors(user, logout, updateTokens, "Error in fetchUserPhoneMasked: " + error);
        Sentry.captureException(error, { extra: { userLoginId: user?.id } });
        toast.error("An error occurred while fetching user phone.");
      } finally {
        span.end();
      }
    });
  }

  const generateOtpBySMS = async (fallbackEmail) => {
    setSmsSending(true);
    try {
      const emailAddress = fallbackEmail || email || localStorage.getItem("otpDrawerEmail") || user?.email || user?.Email;

      if (!emailAddress) {
        Sentry.captureMessage("GenerateOTPBySMS failed: Missing email", "warning");
        toast.error("Unable to send SMS OTP. Missing user details.");
        return;
      }

      setErrors((prev) => ({ ...prev, confirmation: undefined }));

      const req = await fetch(import.meta.env.VITE_API_URL + `Users/GenerateOTPBySMS/`, {
        method: "POST",
        body: JSON.stringify({ email: emailAddress }),
        headers: { "Content-Type": "application/json" },
      });

      if (!req.ok) {
        const errorMsg = `Failed to send OTP by SMS: ${req.statusText}`;
        Sentry.captureMessage(errorMsg, "warning");
        toast.error("Failed to send SMS OTP. Please try again.");
        return;
      }

      toast.success("SMS OTP sent successfully.");
    } catch (error) {
      LogErrors(user, logout, updateTokens, "Error generating OTP by SMS: " + error);
      Sentry.captureException(error);
      toast.error("An error occurred while sending SMS OTP.");
    } finally {
      setSmsSending(false);
    }
  }

  const buildOtpDrawerContent = (otpEmail) => (
    <div className="w-full">
      <div className="flex items-center justify-between w-full">
        <br />
        <h2 className="leading-5 tracking-widest uppercase text-[12px]">
          One Time Password
        </h2>
        <CloseIcon onClick={() => {setOpen(false); setOpenOTPDrawer(false); localStorage.removeItem("otpDrawerOpen");localStorage.removeItem("otpDrawerEmail");localStorage.removeItem("otpDrawerUserLoginId")}} />
      </div>
      <div className="mt-6 flex flex-col gap-2 justify-stretch">
        <h3 className="text-[24px]">Enter your confirmation code</h3>
        <p className=" text-sportsDarkGrey">
          A new confirmation code has been sent to{" "}
          {otpEmail}
        </p>
        <form
          className="flex flex-col gap-2 justify-stretch"
          onSubmit={(e) => otpLogin(e, otpEmail)}
        >
          <TextInput
            label={"Confirmation Code"}
            name="confirmation"
            error={errors?.confirmation}
            htmlError={errors?.confirmation?.includes("href")}
          />
          <div className="flex flex-col gap-4">
            <Button
              onClick={() => resendConfirmation(otpEmail)}
              status="nobg"
              disabled={confirmationSent}
              variant="tertiary"
              className={`disabled:opacity-50`}
            >
              {confirmationSent
                ? <><span className="loading loading-spinner loading-xs" /> Sending...</>
                : "Resend confirmation code"
              }
            </Button>
            <Button submit disabled={otpSubmitting}>
              {otpSubmitting
                ? <><span className="loading loading-spinner loading-sm" /> Verifying...</>
                : "Continue"
              }
            </Button>
            {userPhoneMasked && (
              <>
                <div className='border-t border-[#E3E3E3] text-center relative'>
                  <p className='absolute left-[50%] translate-x-[-50%] top-[-10px] text-sportsBlack font-semibold text-[12px] bg-white px-[9px] tracking-[0.2em]'>Or</p>
                </div>
                <Button
                  variant="quaternary"
                  disabled={smsSending}
                  onClick={(e) => { e.preventDefault(); generateOtpBySMS(otpEmail); }}
                >
                  {smsSending
                    ? <><span className="loading loading-spinner loading-sm" /> Sending...</>
                    : `Text to mobile ending ${userPhoneMasked}`
                  }
                </Button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );

  const triggerOtpDrawer = (email) => {
      //Trigger OTP Login
      localStorage.setItem("otpDrawerOpen", true);
      setOpenOTPDrawer(true);
      if (!userPhoneMasked && email) {
        fetchUserPhoneMasked(email);
      }
      setOpen(true);
      setDrawerContent(buildOtpDrawerContent(email));
    }

    const preLogin = async (event) => {
      event.preventDefault();
      Sentry.startSpan({ name: "PreLogin Attempt", op: "auth" }, async (span) => {
        try {
          setErrors({});
          setEmailAuthenticated(false);
    
          // Clear old session data
          localStorage.removeItem("user");
          localStorage.removeItem("auth_session");
    
          const newDetails = new FormData(event.currentTarget);
          const email = newDetails.get("email");
    
          if (!email) {
            Sentry.captureMessage("Email is missing in preLogin", "warning");
            setErrors({ email: "Please enter your email" });
            return;
          }
    
          const req = await fetch(
            import.meta.env.VITE_API_URL + `Users/CheckUserPreLogin/`,
            {
              method: "POST",
              body: JSON.stringify(email),
              headers: { "Content-Type": "application/json" },
            }
          );
    
          if (!req.ok) {
            const errorMsg = "Email not recognized. Please try again";
            setErrors({ email: errorMsg });
            Sentry.captureMessage(errorMsg, "warning");
            return;
          }
    
          const res = await req.json();
          setEmail(email);
          setEmailAuthenticated(true);
    
          const rawRetVal = res?.retVal;
          let parsedRes;
          if (typeof rawRetVal === "string") {
            try { parsedRes = JSON.parse(rawRetVal); } catch { parsedRes = {}; }
          } else if (rawRetVal !== null && rawRetVal !== undefined && typeof rawRetVal === "object") {
            parsedRes = rawRetVal;
          } else {
            parsedRes = {};
          }
          // Handle case where retVal is a plain primitive ID (number or numeric string)
          const primitiveId = (typeof rawRetVal === "number" || (typeof rawRetVal === "string" && /^\d+$/.test(rawRetVal.trim()))) ? String(rawRetVal) : null;
          const parsedUserLoginId = primitiveId || extractUserLoginId(parsedRes, res);
          if (parsedUserLoginId) {
            setOtpUserLoginId(String(parsedUserLoginId));
            localStorage.setItem("otpDrawerUserLoginId", String(parsedUserLoginId));
          }
          const last4 = parsedRes?.last4 || parsedRes?.Last4 || parsedRes?.ladt4 || parsedRes?.masked || parsedRes?.phoneMasked || parsedRes?.PhoneMasked;
          if (last4) {
            setUserPhoneMasked(String(last4));
          }
          if (parsedRes.OTPRequired === 1) {
            // Trigger OTP Login
            setPassTryCount(0);
            localStorage.setItem("otpDrawerEmail", email);
            triggerOtpDrawer(email);
          } else {
            // Trigger Password Login
            setLoginType("password");
          }
    
          span.setStatus("ok");
        } catch (error) {
          LogErrors(user, logout, updateTokens, "Error in preLogin: " + error);
          Sentry.captureException(error);
          toast.error("An error occurred while logging in.");
        } finally {
          span.end();
        }
      });
    };

    const passLogin = async (event) => {
      event.preventDefault();
      Sentry.startSpan({ name: "Password Login Attempt", op: "auth" }, async (span) => {
        try {
          setErrors({});
          const newDetails = new FormData(event.currentTarget);
    
          const req = await fetch(import.meta.env.VITE_API_URL + `Users/Login/`, {
            method: "POST",
            body: JSON.stringify({
              email: newDetails.get("email"),
              password: newDetails.get("password"),
            }),
            headers: { "Content-Type": "application/json" },
          });
    
          if (!req.ok) {
            const error = await req.text();
            setErrors({ password: error });
            Sentry.captureMessage(`Password login failed: ${error}`, "log");
            return;
          }
    
          const res = await req.json();
          login(res);
          setLocalUser(res);
          handlePostLoginDrawers(res, "");
    
          span.setStatus("ok");
        } catch (error) {
          LogErrors(user, logout, updateTokens, "Error in passLogin: " + error);
          Sentry.captureException(error);
          toast.error("An error occurred while logging in.");
        } finally {
          span.end();
        }
      });
    };

    const otpLogin = async (event, email) => {
      event.preventDefault();
      setOtpSubmitting(true);
      Sentry.startSpan({ name: "OTP Login Attempt", op: "auth" }, async (span) => {
        try {
          setErrors({});
          const newDetails = new FormData(event.currentTarget);
          const otpCode = newDetails.get("confirmation");
    
          if (!otpCode) {
            setErrors({ confirmation: "Please enter the OTP code." });
            return;
          }
    
          const req = await fetch(import.meta.env.VITE_API_URL + `Users/LoginOTP/`, {
            method: "POST",
            body: JSON.stringify({ email, otp: otpCode }),
            headers: { "Content-Type": "application/json" },
          });
    
          if (!req.ok) {
            const errorMsg = await req.text();
            setErrors({ confirmation: errorMsg });
            Sentry.captureMessage(`OTP login failed: ${errorMsg}`, "log");
            return;
          }
    
          const res = await req.json();
          toast.success("Logged in successfully.");
          login(res);
          setLocalUser(res);
          setOpen(false);
          setOpenOTPDrawer(false);
          setLoginType("setPassword");
    
          localStorage.removeItem("otpDrawerOpen");
          localStorage.removeItem("otpDrawerEmail");
          localStorage.removeItem("otpDrawerUserLoginId");
          span.setStatus("ok");
        } catch (error) {
          LogErrors(user, logout, updateTokens, "Error in otpLogin: " + error);
          Sentry.captureException(error);
          toast.error("An error occurred while verifying OTP.");
        } finally {
          setOtpSubmitting(false);
          span.end();
        }
      });
    };

    const resendConfirmation = async (email) => {
      Sentry.startSpan({ name: "Resend OTP", op: "auth" }, async (span) => {
        try {
          if (!email) {
            Sentry.captureMessage("Resend confirmation failed: Email is missing", "warning");
            setErrors({ email: "Email is required." });
            return;
          }
    
          setErrors({});
          setPassTryCount(0);
          setConfirmationSent(true);
    
          setTimeout(() => {
            setConfirmationSent(false);
          }, 5000);
    
          const req = await fetch(import.meta.env.VITE_API_URL + `Users/GenerateOTP/`, {
            method: "POST",
            body: JSON.stringify({ email }),
            headers: { "Content-Type": "application/json" },
          });
    
          if (!req.ok) {
            const errorMsg = `Failed to resend OTP: ${req.statusText}`;
            Sentry.captureMessage(errorMsg, "warning");
            toast.error("Failed to resend OTP. Please try again.");
          } else {
            toast.success("Confirmation code sent.");
          }
          
          span.setStatus(req.ok ? "ok" : "error");
        } catch (error) {
          LogErrors(user, logout, updateTokens, "Error resending OTP: " + error);
          Sentry.captureException(error);
          toast.error("An error occurred while resending OTP.");
        } finally {
          span.end();
        }
      });
    };

    const setPass = async (event) => {
      event.preventDefault();
      Sentry.startSpan({ name: "Set Password", op: "auth" }, async (span) => {
        try {
          setErrors({});
          const newDetails = new FormData(event.currentTarget);
          const newPassword = newDetails.get("newPassword");
          const confirmPassword = newDetails.get("newPassword2");
    
          if (newPassword !== confirmPassword) {
            setErrors({ newPassword2: "Password doesn't match. Try again." });
            return;
          }
    
          const isPasswordValid = () => {
            const minLength = newPassword.length >= 8;
            const hasNumber = /\d/.test(newPassword);
            const hasUppercase = /[A-Z]/.test(newPassword);
            const hasLowercase = /[a-z]/.test(newPassword);
            const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
    
            return minLength && hasNumber && hasUppercase && hasLowercase && hasSpecialChar;
          };
    
          if (!isPasswordValid()) {
            setErrors({ newPassword: "Password does not meet security requirements." });
            return;
          }
    
          const req = await fetch(import.meta.env.VITE_API_URL + `Users/ChangePassword/`, {
            method: "POST",
            body: JSON.stringify({ email: email, password: newPassword }),
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          });
    
          if (!req.ok) {
            setErrors({ newPassword: "Password update failed. Please try again." });
            Sentry.captureMessage("Password change failed", "warning");
            return;
          }
    
          // Password changed successfully
          setOpen(true);
          setDrawerContent(
            <div className="w-full">
              <div className="flex items-center justify-between w-full">
                <br />
                <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                  Password
                </h2>
                <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
              </div>
              <div className="mt-6 flex flex-col gap-4 justify-stretch">
                <h3 className="text-[24px]">Your new password was successfully set</h3>
                <Button
                  className="!bg-sportsYellow !text-sportsBlack"
                  onClick={() => {
                    setOpen(false);
                    handlePostLoginDrawers(user);
                  }}
                >
                  Continue
                </Button>
              </div>
            </div>
          );
    
          span.setStatus("ok");
        } catch (error) {
          LogErrors(user, logout, updateTokens, "Error setting password: " + error);
          Sentry.captureException(error);
          toast.error("An error occurred while setting the password.");
        } finally {
          span.end();
        }
      });
    };

  useEffect(() => {
    if (errors?.confirmation)
    {
      triggerOtpDrawer(email); 
      localStorage.setItem("otpDrawerEmail", email);
    }
  }, [errors?.confirmation]);

  useEffect(() => {
    if (confirmationSent !== null)
    {
      triggerOtpDrawer(email);
      localStorage.setItem("otpDrawerEmail", email);
    }
  }, [confirmationSent]);

  return (
    <>
      {!splashScreen && (
        <SplashScreen />
      )}

      <div className="flex flex-col gap-4 p-6">
        <SGLogoIcon style={{ fill: "black" }} className="w-[40px] h-[40px]" />
        <h1 className={`text-[40px] ${reset ? "mb-0" : "mb-5"}`}>
          Log in to <br />
          Sports Guardian
        </h1>
        {(reset && loginType == "email") && (<p className="text-sportsGreen font-poppins font-bold">To finish resetting your password, please login below.</p>)}
        {loginType === "email" ? (
          <form className="flex flex-col gap-4" onSubmit={preLogin}>
            <TextInput
              key={"email"}
              error={errors.email}
              name="email"
              label="Email"
              placeholder="Enter Email"
            />
            <Button submit>Continue</Button>
          </form>
        ) : loginType === "password" ? (
          <form className="flex flex-col gap-4" onSubmit={passLogin}>
            <TextInput
              defaultValue={email}
              key={"email2"}
              error={errors.email}
              name="email"
              label="Email"
              placeholder="Enter Email"
              readonly={emailAuthenticated ? true : false}
              onClick={() => {emailAuthenticated ? setLoginType("email") : false}}
            />
            <TextInput
              type={"password"}
              key={"password"}
              error={errors.password}
              name="password"
              label="Password"
              placeholder="Enter Password"
              htmlError={true}
            />
            <Button submit>Log In</Button>
          </form>
        ) : (
          loginType === "setPassword" && (
            <form className="flex flex-col gap-4" onSubmit={setPass}>
              <TextInput
                type={"password"}
                key={"newPassword"}
                error={errors.newPassword}
                onChange={(e) => {setPassStrength(passwordStrength(e.target.value).value), setPassword(e.target.value)}}
                name="newPassword"
                label="Enter Your New Password"
                placeholder="Enter Password"
              />
              <div className="border-[1px] border-[#E3E3E3] flex flex-col gap-5 p-5">
                <p className="font-medium">Password Must Contain:</p>
                <div>
                  <ul className="flex flex-col gap-1">
                    <li className="flex items-center gap-2"><input type='checkbox' name='minChar' className={`checkbox rounded-full passwordCheck`} checked={password.length >= 8}  /> 8 characters minimum</li>
                    <li className="flex items-center gap-2"><input type='checkbox' name='number' className={`checkbox rounded-full passwordCheck`} checked={/\d/.test(password)}  /> At least 1 number</li>
                    <li className="flex items-center gap-2"><input type='checkbox' name='uppercase' className={`checkbox rounded-full passwordCheck`} checked={/[A-Z]/.test(password)}  /> At least 1 uppercase letter</li>
                    <li className="flex items-center gap-2"><input type='checkbox' name='lowercase' className={`checkbox rounded-full passwordCheck`} checked={/[a-z]/.test(password)}  /> At least 1 lowercase letter</li>
                    <li className="flex items-center gap-2"><input type='checkbox' name='specChar' className={`checkbox rounded-full passwordCheck`} checked={/[!@#$%^&*(),.?":{}|<>]/.test(password)}  /> At least 1 special character</li>
                  </ul>
                </div>
                <div>
                   <p className="font-medium">Strength:</p>
                    <div className="flex gap-1 mt-5">
                      <div className={`${passStrength === 'Too weak' ? 'bg-red-500' : passStrength === 'Weak' || passStrength === 'Medium' ? 'bg-yellow-500' : passStrength === 'Strong' ? 'bg-green-500' : 'bg-[#E3E3E3]'} w-1/3 h-[4px]`} />
                      <div className={`${passStrength === 'Weak' || passStrength === 'Medium' ? 'bg-yellow-500' : passStrength === 'Strong' ? 'bg-green-500' : 'bg-[#E3E3E3]'} w-1/3 h-[4px]`}  />
                      <div className={`${passStrength === 'Strong' ? 'bg-green-500' : 'bg-[#E3E3E3]'} w-1/3 h-[4px]`}  />
                    </div>
                  </div>
              </div>
              <TextInput
                type={"password"}
                key={"newPassword2"}
                error={errors.newPassword2}
                name="newPassword2"
                label="Re-Enter Your New Password"
                placeholder="Enter Password"
              />
              <Button submit>Continue</Button>
            </form>
          )
        )}
        <Link className="flex flex-col justify-stretch" to={"/password-reset"}>
          {" "}
          <Button status="nobg" variant="tertiary">
            I&apos;ve Forgotten My Password
          </Button>{" "}
        </Link>
      </div>
    </>
  );
}
